import { useEffect, useRef, useState } from "react";
import { api, API, formatApiError, getToken } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Upload, FileText, Image as ImageIcon, Eye, Download, Trash2, FolderOpen } from "lucide-react";
import ViewDocumentDialog from "@/components/ViewDocumentDialog";

function formatSize(bytes) {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let size = bytes;
  while (size >= 1024 && i < units.length - 1) {
    size /= 1024;
    i++;
  }
  return `${size.toFixed(size < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

export default function DocumentsTab({ propertyId }) {
  const { user } = useAuth();
  const isAdmin = user && user !== false && user.role === "admin";
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [viewing, setViewing] = useState(null);
  const fileInputRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/properties/${propertyId}/documents`);
      setFiles(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      await api.post(`/properties/${propertyId}/documents`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("File uploaded");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDownload = async (file) => {
    const token = getToken() || "";
    const url = `${API}/properties/${propertyId}/documents/${file.id}/download?auth=${encodeURIComponent(token)}`;
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error("Download failed");
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = file.original_filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5_000);
    } catch {
      // Fallback for very large files where blob() fails — open the URL directly
      window.open(url, "_blank", "noreferrer");
    }
  };

  const handleDelete = async (file) => {
    if (!window.confirm(`Delete "${file.original_filename}"?`)) return;
    try {
      await api.delete(`/properties/${propertyId}/documents/${file.id}`);
      toast.success("File deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-medium text-stone-900">Documents</h2>
          <p className="text-sm text-stone-500 mt-1">PDFs and images related to this property.</p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
          onChange={handleUpload}
          className="hidden"
          data-testid="file-input"
        />
        {isAdmin && (
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="bg-emerald-900 hover:bg-emerald-800 text-stone-50"
            data-testid="upload-button"
          >
            <Upload className="h-4 w-4 mr-2" strokeWidth={1.5} />
            {uploading ? "Uploading…" : "Upload file"}
          </Button>
        )}
      </div>

      {loading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-14 rounded-md bg-white border border-stone-200 animate-pulse" />
          ))}
        </div>
      ) : files.length === 0 ? (
        <Card className="border border-dashed border-stone-300 bg-white p-12 text-center rounded-xl">
          <FolderOpen className="h-10 w-10 text-stone-300 mx-auto mb-3" strokeWidth={1.5} />
          <h3 className="text-lg font-medium text-stone-900">No documents yet</h3>
          <p className="text-stone-500 mt-1 text-sm">Upload your rental agreement, GST receipts, or property photos.</p>
        </Card>
      ) : (
        <Card className="border-stone-200 bg-white rounded-xl overflow-hidden">
          <ul className="divide-y divide-stone-100">
            {files.map((f) => {
              const isImg = (f.content_type || "").startsWith("image/");
              return (
                <li key={f.id} className="flex items-center gap-4 p-4 hover:bg-stone-50 transition-colors" data-testid={`file-row-${f.id}`}>
                  <div className="h-10 w-10 rounded-md bg-stone-100 flex items-center justify-center text-stone-600 shrink-0">
                    {isImg ? <ImageIcon className="h-5 w-5" strokeWidth={1.5} /> : <FileText className="h-5 w-5" strokeWidth={1.5} />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-stone-900 font-medium truncate">{f.original_filename}</p>
                    <p className="text-xs text-stone-500 mt-0.5">
                      {formatSize(f.size)} · {formatDate(f.created_at)}
                    </p>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => setViewing(f)} className="text-stone-500 hover:text-emerald-900" data-testid={`view-${f.id}`}>
                    <Eye className="h-4 w-4" strokeWidth={1.5} />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDownload(f)} className="text-stone-500 hover:text-emerald-900" data-testid={`download-${f.id}`}>
                    <Download className="h-4 w-4" strokeWidth={1.5} />
                  </Button>
                  {isAdmin && (
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(f)} className="text-stone-500 hover:text-red-700" data-testid={`delete-file-${f.id}`}>
                      <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      <ViewDocumentDialog
        propertyId={propertyId}
        file={viewing}
        open={!!viewing}
        onOpenChange={(v) => !v && setViewing(null)}
      />
    </div>
  );
}
